import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { Blockchain } from "@anaconda/core/blockchain/types"
import {
  type OrderId,
  type SolanaCancelByOrderRequest,
  cancelByOrderFactoryReatom,
} from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { CancelByOrderToast } from "../../toasts/cancel-by-order"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

export interface SolanaCancelByOrderActionParams {
  orderId: OrderId
}

export interface SolanaCancelByOrderActionResult {
  transactionId: string
  orderId: OrderId
}

export const SolanaCancelByOrderTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)

  const cancelByOrderActionFactory = () => {
    const cancelByOrderAction = action(
      async ({ orderId }: SolanaCancelByOrderActionParams) => {
        const container = sdkContainerAtom()
        const cancelByOrder = cancelByOrderFactoryReatom(container)
        const request: SolanaCancelByOrderRequest = { orderId }

        const transactionId = await wrap(cancelByOrder(request))

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CancelByOrderToast.Success
            id={id}
            transactionId={transactionId}
            blockchain={Blockchain.SOLANA}
          />
        ))

        return { transactionId, orderId }
      },
      "solanaCancelByOrderAction",
    ).extend(
      withAsync(),
      withCallHook(async (_values, [params]) => {
        const { orderId } = params

        sonnerToast.custom((id) => <CancelByOrderToast.Pending id={id} />, {
          duration: Number.POSITIVE_INFINITY,
        })

        const baseTransactionEventContext: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
        }

        logger?.api.pushEvent(
          TransactionEvent.Started,
          baseTransactionEventContext,
        )
        amplitude.track(TransactionEvent.Started, baseTransactionEventContext)
      }),
    )

    cancelByOrderAction.onFulfill.extend(
      withCallHook((result) => {
        const { transactionId, orderId } = result.payload

        const context: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
          transactionId,
        }

        logger?.api.pushEvent(TransactionEvent.Completed, context)
        amplitude.track(TransactionEvent.Completed, context)
      }),
    )

    cancelByOrderAction.onReject.extend(
      withCallHook(({ error, params: [paramsValue] }) => {
        console.error("Solana cancel by order failed:", error)

        const { orderId } = paramsValue

        const context: EventContext = {
          flow: Flow.CancelOffer,
          order: orderId,
          error: getTransactionErrorMessage(error),
        }

        logger?.api.pushEvent(TransactionEvent.Failed, context)
        amplitude.track(TransactionEvent.Failed, context)

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CancelByOrderToast.Error id={id} error={error as Error} />
        ))
      }),
    )

    return cancelByOrderAction
  }

  return { cancelByOrderActionFactory }
})
